'use strict'
/**
 * Onboarding V2 · draft persistence
 * localStorage guarda answers + step actual. El usuario puede salir y volver
 * sin perder progreso.
 *
 * QA-E2E bug#2 (2026-06-13): el draft se filtraba entre cuentas en el mismo
 * navegador porque (a) el logout borraba la clave en MAYÚSCULAS (PL_ONBOARDING_DRAFT,
 * inexistente) en vez de la real, y (b) la clave no estaba scopeada por usuario.
 * Fix: clave scopeada por userId + clearAllOnboardingV2Drafts() en TODOS los logout.
 */
const BASE = 'pl_onboarding_v2_draft'

/**
 * SALTO:onboarding-10 · T2 · decisión del founder (2026-09-14): «invitado hasta "Tu plan": el borrador sin cuenta y su traspaso al crearla». La cuenta
 * va al final, no al principio.
 *
 * El borrador del invitado tiene **su propio dueño con nombre**, no la clave global. Eso importa: la clave global fue la fuga de QA-E2E bug#2 (el
 * borrador de una cuenta se heredaba en otra del mismo navegador), y por eso `loadDraft(null)` devolvía vacío. Un dueño llamado `invitado` conserva la
 * regla —dos usuarios nunca comparten clave— y además entra en el barrido de `clearAllOnboardingV2Drafts` sin tocar nada, porque lleva el mismo prefijo.
 */
export const INVITADO = 'invitado'

// Clave por usuario, o la del invitado si todavía no hay cuenta. Nunca la clave global (esa era la fuga entre cuentas).
function keyFor(userId) {
  return `${BASE}__${userId || INVITADO}`
}

export function loadDraft(userId) {
  try {
    const raw = window.localStorage.getItem(keyFor(userId))
    if (!raw) return { answers: {}, step: 0 }
    const parsed = JSON.parse(raw)
    return {
      answers: parsed?.answers || {},
      step: typeof parsed?.step === 'number' ? parsed.step : 0,
      savedAt: parsed?.savedAt || null,
    }
  } catch (_) { return { answers: {}, step: 0 } }
}

export function saveDraft(answers, step, userId) {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify({ answers, step, savedAt: Date.now() }))
  } catch (_) {}
}

export function clearDraft(userId) {
  try { window.localStorage.removeItem(keyFor(userId)) } catch (_) {}
}

/**
 * SALTO:onboarding-10 · T2 · EL TRASPASO. Al crear la cuenta, el borrador del invitado pasa a ser el suyo y el del invitado se borra.
 *
 * Sólo se traspasa si el usuario **no tiene ya el suyo**: quien vuelve a su alta a medias no puede perderla porque alguien dejara un borrador de
 * invitado en este navegador. Y el del invitado se borra siempre que se traspasa: si se quedara, la siguiente cuenta que se diera de alta aquí
 * heredaría las respuestas de otra persona — que es exactamente la fuga que la clave por usuario cerró.
 *
 * @returns {boolean} si hubo traspaso
 */
export function traspasarBorradorDeInvitado(userId) {
  if (!userId || userId === INVITADO) return false
  try {
    const delInvitado = window.localStorage.getItem(keyFor(INVITADO))
    if (!delInvitado) return false
    const suyo = window.localStorage.getItem(keyFor(userId))
    const tieneAlgo = (() => { try { return Object.keys((JSON.parse(suyo) || {}).answers || {}).length > 0 } catch (_) { return false } })()
    if (!tieneAlgo) window.localStorage.setItem(keyFor(userId), delInvitado)
    window.localStorage.removeItem(keyFor(INVITADO))
    return !tieneAlgo
  } catch (_) { return false }
}

/**
 * Limpieza total (logout / cambio de cuenta): borra la clave legacy global +
 * TODAS las claves scopeadas por usuario (incluida la del invitado, que lleva el mismo prefijo).
 */
export function clearAllOnboardingV2Drafts() {
  try {
    const toRemove = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k === BASE || (k && k.startsWith(BASE + '__'))) toRemove.push(k)
    }
    toRemove.forEach(k => window.localStorage.removeItem(k))
  } catch (_) {}
}
